import type React from 'react';
import {useState} from 'react';
import {Box, Button, Collapse, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import type {PriceModel} from '../../types/shouldCostTypes';
import {ProfileSection} from '../components/ProfileSection';
import {SHOULD_COST_HELP} from '../../domain/shouldCostHelp';

/** Model provenance (spec 4.9): where each line's value comes from and what it assumes, plus the methodology. */
export const ProvenanceCard: React.FC<{model: PriceModel}> = ({model}) => {
    const [method, setMethod] = useState(false);
    return (
        <ProfileSection title="Model provenance" help={SHOULD_COST_HELP.provenance}
                        actions={<Button size="small" onClick={() => setMethod(!method)} aria-expanded={method}>Methodology</Button>}>
            <Stack gap={1.5}>
                <Collapse in={method}>
                    <Box sx={{p: 1.5, bgcolor: 'grey.50', borderRadius: 1}}>
                        <Typography variant="body2">
                            Each cost line has a cost per unit in the base month. A line with a cost driver moves with that
                            market index from the base month to its latest value; a line without one stays fixed. Should cost
                            is the sum of the lines. A quote is compared in total and, when broken down, line by line: up to
                            the validate threshold the gap is accepted, up to the challenge threshold it is validated, above
                            it is challenged. A line marked calibrated was corrected earlier from supplier quotes. The accuracy score
                            weights each line's share of cost by its data tier (live 1.0, modeled 0.8, manual 0.6, estimated 0.4).
                        </Typography>
                    </Box>
                </Collapse>
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small" aria-label="Model provenance">
                        <TableHead>
                            <TableRow>
                                <TableCell>Line item</TableCell>
                                <TableCell>Value type</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell>Embedded assumption</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {model.provenance.map(p => (
                                <TableRow key={p.lineId}>
                                    <TableCell>{p.name}</TableCell>
                                    <TableCell>{p.valueType}</TableCell>
                                    <TableCell>{p.source}</TableCell>
                                    <TableCell>{p.assumption}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Stack>
        </ProfileSection>
    );
};
